import { useEffect, useRef, useState } from "react";
import { KeyRound } from "lucide-react";

import { Overlay } from "./Overlay";

const TOKEN_KEY = "sa.adminToken";

interface AdminTokenDialogProps {
  open: boolean;
  symbol: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: (token: string) => void;
}

// Replaces window.prompt for the companion-agent refresh flow. The token is
// remembered for the browser session so repeat refreshes don't re-ask.
export function AdminTokenDialog({
  open,
  symbol,
  busy,
  onClose,
  onSubmit,
}: AdminTokenDialogProps) {
  const [token, setToken] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    let saved = "";
    try {
      saved = sessionStorage.getItem(TOKEN_KEY) || "";
    } catch {
      // Session storage may be unavailable; start empty.
    }
    setToken(saved);
    const id = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(id);
  }, [open]);

  if (!open) {
    return null;
  }

  const submit = () => {
    const trimmed = token.trim();
    if (!trimmed) {
      return;
    }
    try {
      sessionStorage.setItem(TOKEN_KEY, trimmed);
    } catch {
      // Best-effort persistence only.
    }
    onSubmit(trimmed);
  };

  return (
    <Overlay onClose={busy ? () => {} : onClose}>
      <form
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-token-title"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <span className="modal-icon" aria-hidden>
          <KeyRound />
        </span>
        <h2 id="admin-token-title" className="modal-title">
          Refresh companion agents
        </h2>
        <p className="modal-copy">
          Enter the admin token to regenerate companion insights for{" "}
          <strong>{symbol}</strong>.
        </p>
        <input
          ref={inputRef}
          type="password"
          className="modal-input"
          placeholder="Admin token"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          autoComplete="off"
        />
        <div className="modal-actions">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy || !token.trim()}>
            {busy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </form>
    </Overlay>
  );
}
